import { NavLink, useNavigate } from 'react-router-dom';
import { SignOut as LogOut, List as Menu, X } from '@phosphor-icons/react';
import { useState } from 'react';
import { useAuth } from '../lib/auth';
import { SeasonalEntry, useSeasonArt } from '../theme/seasonal';
import TwitchSignInLabel from './TwitchSignInLabel';

// Kept to the core destinations; Docs and Changelog live in the footer.
// The /support route keeps its slug, only the label reads "Membership".
const NAV: { to: string; label: string; external?: boolean }[] = [
  { to: '/downloads', label: 'Downloads' },
  { to: '/overlays', label: 'Overlays' },
  { to: '/bots', label: 'Bots' },
  { to: '/plugins', label: 'Plugins' },
  { to: '/support', label: 'Membership' },
];

export default function Header() {
  const { me, loading, login, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const logo = useSeasonArt('logo') ?? '/streamnook-logo.png';

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-20 w-full px-4 pt-3 sm:px-6">
      <div className="mx-auto max-w-6xl">
        <div className="site-header-bar flex h-14 items-center gap-3 rounded-2xl border border-[var(--color-border)] bg-[rgba(12,13,15,0.6)] px-3 backdrop-blur-xl shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05),0_10px_30px_-10px_rgba(0,0,0,0.6)] sm:px-4">
        <NavLink
          to="/"
          onClick={() => setOpen(false)}
          className="flex items-center gap-2 font-medium tracking-wide shrink-0"
        >
          <img src={logo} alt="" className="site-logo w-7 h-7 object-contain" />
          <span className="flex flex-col leading-none">
            <span className="text-textPrimary">StreamNook</span>
            <span className="self-end font-serif italic text-[11px] leading-none text-textMuted">by Penrose</span>
          </span>
        </NavLink>

        {/* Inline nav, tablet and up. Below 1024 px the nav and the account
            controls are a step tighter and smaller: at full size they ran past
            the bar's right edge from 640 px up. */}
        <nav className="hidden items-center text-[12px] sm:flex lg:ml-4 lg:gap-1 lg:text-[13px]">
          {NAV.map(n =>
            n.external ? (
              <a
                key={n.to}
                href={n.to}
                className="rounded-md px-1 py-1 transition-colors text-textSecondary hover:text-textPrimary hover:bg-white/[0.03] lg:px-2.5"
              >
                {n.label}
              </a>
            ) : (
              <NavItem key={n.to} to={n.to} label={n.label} />
            )
          )}
        </nav>

        {/* Inline auth — tablet and up */}
        <div className="ml-auto hidden items-center gap-1 text-sm sm:flex">
          <SeasonalEntry variant="bar" className="hidden lg:flex" />
          {loading ? null : me ? (
            <>
              {me.is_admin && <NavItem to="/admin" label="Admin" />}
              <NavLink
                to="/dashboard"
                title="Dashboard"
                className={({ isActive }) =>
                  `flex items-center gap-2 px-1 py-1 rounded-md transition-colors lg:px-2.5 ${
                    isActive ? 'text-textPrimary bg-[var(--color-surface)]' : 'text-textSecondary hover:text-textPrimary hover:bg-white/[0.03]'
                  }`
                }
              >
                <img src={me.avatar_url} alt="" className="w-6 h-6 rounded-full" />
                <span className="sr-only lg:not-sr-only lg:max-w-[6rem] lg:truncate xl:max-w-[10rem]">{me.display_name}</span>
              </NavLink>
              <button
                onClick={handleLogout}
                className="p-1 rounded-md text-textSecondary lg:ml-1 lg:p-1.5 hover:text-textPrimary hover:bg-white/[0.03] transition-colors"
                title="Sign out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <button onClick={login} className="glass-button px-3 py-1.5 text-sm">
              <TwitchSignInLabel />
            </button>
          )}
        </div>

        {/* Mobile cluster — avatar (if signed in) + hamburger */}
        <div className="ml-auto flex items-center gap-2 sm:hidden">
          {me && (
            <NavLink to="/dashboard" onClick={() => setOpen(false)} aria-label="Dashboard">
              <img src={me.avatar_url} alt="" className="w-7 h-7 rounded-full" />
            </NavLink>
          )}
          <button
            onClick={() => setOpen(o => !o)}
            aria-label="Menu"
            aria-expanded={open}
            className="-mr-2 p-2 rounded-md text-textSecondary hover:text-textPrimary hover:bg-white/[0.03] transition-colors"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {open && (
        <div className="site-header-menu mt-2 rounded-2xl border border-[var(--color-border)] bg-[rgba(12,13,15,0.95)] px-3 py-3 backdrop-blur-xl sm:hidden">
          <nav className="space-y-1">
            {NAV.map(n =>
              n.external ? (
                <a
                  key={n.to}
                  href={n.to}
                  onClick={() => setOpen(false)}
                  className="block rounded-md px-3 py-2.5 text-sm transition-colors text-textSecondary hover:bg-white/[0.03] hover:text-textPrimary"
                >
                  {n.label}
                </a>
              ) : (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `block rounded-md px-3 py-2.5 text-sm transition-colors ${
                      isActive ? 'bg-[var(--color-surface)] text-textPrimary' : 'text-textSecondary hover:bg-white/[0.03] hover:text-textPrimary'
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              )
            )}
            <SeasonalEntry variant="menu" onNavigate={() => setOpen(false)} />
            {me?.is_admin && (
              <NavLink
                to="/admin"
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block rounded-md px-3 py-2.5 text-sm transition-colors ${
                    isActive ? 'bg-[var(--color-surface)] text-textPrimary' : 'text-textSecondary hover:bg-white/[0.03] hover:text-textPrimary'
                  }`
                }
              >
                Admin
              </NavLink>
            )}
          </nav>
          <div className="mt-2 border-t border-[var(--color-borderSubtle)] pt-2">
            {loading ? null : me ? (
              <button
                onClick={handleLogout}
                className="block w-full rounded-md px-3 py-2.5 text-left text-sm text-error transition-colors hover:bg-white/[0.03]"
              >
                Sign out
              </button>
            ) : (
              <button
                onClick={() => { setOpen(false); login(); }}
                className="glass-button block w-full rounded-md px-3 py-2.5 text-center text-sm font-medium"
              >
                <TwitchSignInLabel />
              </button>
            )}
          </div>
        </div>
      )}
      </div>
    </header>
  );
}

function NavItem({ to, label }: { to: string; label: string }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `rounded-md px-1 py-1 transition-colors lg:px-2.5 ${
          isActive
            ? 'text-textPrimary bg-[var(--color-surface)]'
            : 'text-textSecondary hover:text-textPrimary hover:bg-white/[0.03]'
        }`
      }
    >
      {label}
    </NavLink>
  );
}
